import {isBlank} from '../../../utils/utils';
import {isNull} from 'lodash';
import {entryHasNoOverrideToOffer} from '../../../reducers/PatEligibilityReducer';

export default function compareVehicles(left, right) {
    if (left === null && right === null) {
        return true
    }

    if (left === null || right === null) {
        return false
    }

    return left.runNumber === right.runNumber
        && left.laneNumber === right.laneNumber
        && left.workOrderNumber === right.workOrderNumber
        && left.sblu === right.sblu
        && left.vin === right.vin
        && left.resultType === right.resultType
}

export function resetVehicleDetailInput(vehicles) {
    const index = vehicles.findIndex(vehicle => vehicle.vehicleDetail && !isNull(vehicle.vehicleDetail.input))
    if (index !== -1) {
        vehicles[index].vehicleDetail.input = null
    }
}

// US-Send-to-AS400-2nd, FR-001/FR-006: whether an already-sent vehicle's live sale type selection
// (patEntry) differs from the sale type reference it was actually last sent to AS400 with
// (vehicle.vehicleSaleType) - the baseline settled on in spec.md Clarifications Q1. Only meaningful
// for a vehicle with a live patEntry (i.e. currently under Edit); a discarded/absent entry, or one
// still loading/errored, is never "changed" (nothing decided yet to compare against).
//
// FR-006i (bug fix, 2026-08-18): a resolved check with nothing eligible to offer still needs its own
// comparison, not an automatic "unchanged" - its effective value is always '' (Lane Default,
// getVehicleSaleType's own FR-006i branch), so if the vehicle's last-sent value was anything else
// (e.g. a since-invalidated sale type code no longer eligible), that's a real change Send to AS400
// needs to pick up, not one it silently drops for lack of an override to pick from.
export function isSaleTypeChangedSinceLastSend(vehicle, patEntry) {
    if (!patEntry || patEntry.status !== 'resolved') {
        return false
    }
    const lastSentSaleTypeRef = vehicle.vehicleSaleType || ''
    if (entryHasNoOverrideToOffer(patEntry)) {
        return lastSentSaleTypeRef !== ''
    }
    const currentSaleTypeRef = patEntry.isReverted ? '' : (patEntry.selectedSaleType?.saleTypeRef ?? '')
    return currentSaleTypeRef !== lastSentSaleTypeRef
}

// Whether a vehicle's live Edit session (patAssignmentInstanceId set) has settled with nothing
// actually decided, so a Send to AS400 / Save As Draft that doesn't include this vehicle can treat it
// as implicitly cancelled and collapse the row back to the read-only FR-015 chip without losing
// anything. Shared by window.resetVehiclesSentToAS400 and window.resetPatAssignmentAfterSaveDraft
// (AssignVehicleList.js) so the two reset paths can't drift apart.
//
// A still-'loading' entry is deliberately NOT clean (bug fix, 2026-08-24): isSaleTypeChangedSinceLastSend
// can only ever report "unchanged" for it - its first guard rejects any non-resolved status, since
// nothing has come back yet to compare against. Without the explicit status check here, an Edit whose
// PAT request is still in flight would have its patAssignmentInstanceId cleared mid-request: the user's
// Edit UI collapses to the read-only chip, and the response landing moments later is orphaned in the
// reducer under a key nothing renders any more (unlike a real Cancel, neither reset path dispatches
// discardPatEligibility). Every other state - resolved-and-unchanged, 'error', or no entry at all - has
// settled, so it stays clean.
export function isSettledCleanEditSession(vehicle, patEntry) {
    return patEntry?.status !== 'loading' && !isSaleTypeChangedSinceLastSend(vehicle, patEntry)
}

// FR-009: identity comparison independent of position - two vehicle records are the "same vehicle"
// if VIN and work order number both match, regardless of runNumber/laneNumber. Deliberately separate
// from compareVehicles() (position-based, used elsewhere for list diffing) - not a replacement for it.
export function isSameVehicleIdentity(vehicleA, vehicleB) {
    if (!vehicleA || !vehicleB) {
        return false
    }
    return vehicleA.vin === vehicleB.vin && vehicleA.workOrderNumber === vehicleB.workOrderNumber
}

// Like the find-and-update-only pattern used elsewhere for draftVehicles (see updateElementInList in
// AssignVehicleList.js), but inserts a new entry when no match is found instead of no-op'ing -
// needed to (re-)add an already-sent vehicle to draftVehicles when its sale type becomes dirty.
// Pure (returns the new array) rather than calling a setState function directly, so a caller can
// upsert/remove several vehicles in a single pass (e.g. one effect reacting to several patEntry
// changes at once) and commit the result with exactly one setState call.
export function upsertElementInList(list, vehicleData, matchFn) {
    const existingIndex = list.findIndex(item => matchFn(item, vehicleData))
    if (existingIndex === -1) {
        return [...list, vehicleData]
    }
    const newList = [...list]
    newList[existingIndex] = {...newList[existingIndex], ...vehicleData}
    return newList
}

// Removes the first entry matching matchFn from list, if present; returns list unchanged (same
// reference) otherwise. Pure - see upsertElementInList above.
export function removeElementFromList(list, vehicleData, matchFn) {
    const existingIndex = list.findIndex(item => matchFn(item, vehicleData))
    if (existingIndex === -1) {
        return list
    }
    return [...list.slice(0, existingIndex), ...list.slice(existingIndex + 1)]
}